import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Download, Printer, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

// Brand-consistent QR: high-contrast navy on white for reliable scanning.
const QR_FG = "#082D50";
const QR_BG = "#FFFFFF";

export function QRBlock({
  value,
  size = 200,
  fileName = "friday-conversations-qr",
  printTitle = "Friday Conversations",
  printSubtitle = "",
  showActions = true,
  className = "",
  testId = "qr-block",
}) {
  const wrapRef = useRef(null);

  const getCanvas = () => wrapRef.current?.querySelector("canvas");

  const downloadPng = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${fileName}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR code downloaded");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Link copied to clipboard");
    } catch (e) {
      toast.error("Could not copy the link");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: printTitle, text: printSubtitle, url: value });
      } catch (e) {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  const print = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=560,height=720");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups to print");
      return;
    }
    // Build the print document with safe DOM APIs (no document.write / HTML string
    // interpolation) so user-supplied values can never inject markup/scripts.
    const doc = w.document;
    doc.title = printTitle;

    const style = doc.createElement("style");
    style.textContent = `
      body{font-family:Manrope,system-ui,sans-serif;display:flex;flex-direction:column;
        align-items:center;justify-content:center;min-height:100vh;margin:0;color:#141414;background:#FBF7F2;}
      .card{background:#fff;border:1px solid #E7DED4;border-radius:20px;padding:40px;text-align:center;
        box-shadow:0 18px 50px rgba(20,20,20,0.08);}
      h1{font-family:Georgia,serif;font-size:24px;margin:0 0 4px;}
      p{color:#6B6B6B;margin:0 0 24px;font-size:14px;}
      img{width:280px;height:280px;}
      .url{margin-top:18px;font-size:12px;color:#6B6B6B;word-break:break-all;max-width:280px;}
    `;
    doc.head.appendChild(style);

    const card = doc.createElement("div");
    card.className = "card";

    const h1 = doc.createElement("h1");
    h1.textContent = printTitle;
    card.appendChild(h1);

    if (printSubtitle) {
      const p = doc.createElement("p");
      p.textContent = printSubtitle;
      card.appendChild(p);
    }

    const img = doc.createElement("img");
    img.alt = "QR code";
    img.addEventListener("load", () => {
      setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 250);
    });
    img.src = dataUrl;
    card.appendChild(img);

    const url = doc.createElement("div");
    url.className = "url";
    url.textContent = value;
    card.appendChild(url);

    doc.body.appendChild(card);
  };

  return (
    <div className={`flex flex-col items-center ${className}`} data-testid={testId}>
      <div ref={wrapRef} className="rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(20,20,20,0.06),0_12px_30px_rgba(20,20,20,0.06)]">
        <QRCodeCanvas
          value={value || " "}
          size={size}
          fgColor={QR_FG}
          bgColor={QR_BG}
          level="M"
          marginSize={2}
          data-testid={`${testId}-canvas`}
        />
      </div>
      {showActions && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <Button size="sm" variant="secondary" onClick={downloadPng} data-testid={`${testId}-download`}>
            <Download className="mr-1.5 h-4 w-4" /> Download
          </Button>
          <Button size="sm" variant="secondary" onClick={print} data-testid={`${testId}-print`}>
            <Printer className="mr-1.5 h-4 w-4" /> Print
          </Button>
          <Button size="sm" variant="secondary" onClick={copyLink} data-testid={`${testId}-copy`}>
            <Copy className="mr-1.5 h-4 w-4" /> Copy link
          </Button>
          <Button size="sm" variant="secondary" onClick={share} data-testid={`${testId}-share`}>
            <Share2 className="mr-1.5 h-4 w-4" /> Share
          </Button>
        </div>
      )}
    </div>
  );
}
